<template>
  <v-app
    class="light--text"
    v-resize="handleOnResize"
    v-scroll="handleOnScroll"
    :class="isAsideFullHeight ? 'full-height' : ''"
  >
    <AppLoader isFixed :isLoading="isAppLoading" />
    <router-view />
  </v-app>
</template>

<script>
import Vue from "vue";
import { NavBar, AsideNavBar, $eventHub } from "@/components";
import { EventConstraints } from "@/enums/events.enum";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { Helper, Lang } from "./services/helper";
import "./styles/index.scss";
import { StorageConstants } from "@/enums/storage.enum";
import Storage from "@/services/storage";
import { LinksConstrains } from "@/enums/links.enum";
import { AppsConstraints } from "@/enums/apps.enum";

let pusher;

export default Vue.extend({
  name: "App",
  components: {
    NavBar,
    AsideNavBar,
  },

  data() {
    return {
      isLoading: true,
      isAsideFullHeight: false,
      online: true,
      wasOnline: true,
    };
  },

  async beforeMount() {
    await this.$store.dispatch("User/singleSignOn");
    await Lang.prepare();
    this.$vuetify.lang.current = this.$store.getters["App/lang"];
    this.$vuetify.rtl = this.$store.getters["App/isRtl"];
    // window["errors"];
    // connectivity status
    this.online = navigator.onLine;

    window.addEventListener("online", () => {
      this.online = true;
    });

    window.addEventListener("offline", () => {
      this.online = false;
      this.wasOnline = false;
    });

    window.addEventListener(
      "beforeunload",
      function () {
        this.$destroy();
        delete this.online;
        delete this.isLoading;
        delete this.isAsideFullHeight;
      }.bind(this)
    );
  },

  watch: {
    online: {
      immediate: true,
      deep: true,
      handler(status) {
        let message = null;
        let success = null;
        if (!status) {
          message = this.$t("no_internet_message");
          success = false;
        }

        if (!!status && !this.wasOnline) {
          message = this.$t("internet_back_message");
          success = true;
        }

        if (!!message) {
          this.$toast(message, {
            y: "bottom",
            x: "right",
            color: success ? "success" : "error",
            showClose: true,
            timeout: 12000,
          });
        }
      },
    },

    isLoggedIn: {
      immediate: true,
      handler(isLoggedIn) {
        if (isLoggedIn) {
          this.initApp();
        }
      },
    },

    isAuthorized: {
      immediate: true,
      handler(isAuthorized) {
        // when user logged in and not authorized=> it will logout immediately
        // && this.$route?.meta?.app !== AppsConstraints.OOC
        if (this.isLoggedIn && !isAuthorized) {
          (async () => {
            await this.doLogOut();
            await this.$router.replace(LinksConstrains.LOGOUT).catch(() => {});
          })();
        }
      },
    },
  },

  mounted() {
    $eventHub.$emit(EventConstraints.HIDE_APP_LOADER);
    this.handleOnAppStorageVersion();
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 500);
  },

  destroyed() {
    window.removeEventListener("online", null);
    window.removeEventListener("offline", null);
    window.removeEventListener("beforeunload", null);
    $eventHub.$off(EventConstraints.SHOW_APP_LOADER, null);
    $eventHub.$off(EventConstraints.HIDE_APP_LOADER, null);

    this.destroyBack4App();
  },

  computed: {
    ...mapGetters({
      isLoggedIn: "User/isLoggedIn",
      session: "User/session",
      isAuthorized: "User/isAuthorized",
      isAppLoading: "App/isLoading",
      oocIsLoggedIn: "OOCUsers/oocIsLoggedIn",
    }),

    showNavigation() {
      return this.isLoggedIn && this.$route.meta.isAuth;
    },
  },

  methods: {
    ...mapActions({
      getCities: "Addresses/getCities",
      getBusiness: "Business/getBusiness",
      getLocations: "Business/getLocations",
      doLogOut: "User/doLogOut",
      setMyOOCProfile: "OOCUsers/setProfile",
    }),

    ...mapMutations({
      setUserAuthorized: "User/SET_IS_USER_AUTHORIZED",
      setUserMessage: "User/SET_IS_USER_MESSAGE",
      destroyBack4App: "Business/DESTROY_BRANCHES_STATUES",
    }),

    /***
     * Handle on Init App
     * Setup App Required Data and Config
     * @return {void}
     */
    initApp() {
      // await this.getBusiness();
      // await this.getLocations();
      this.setupDashboard();
      this.setupOOCApp();
    },

    /**
     * Setup the dashboard
     * @return {void}
     */
    setupDashboard() {
      if (!this.isLoggedIn) {
        return;
      }

      const country = Storage.get(StorageConstants.BUSINESS).country;
      this.getCities(country);
    },

    /**
     * Setup OOC App
     * @return {void}
     */
    async setupOOCApp() {
      if (Helper.getURIAppName() !== AppsConstraints.OOC) {
        return;
      }
      // setup the ooc session
      await this.setMyOOCProfile();
      if (!this.oocIsLoggedIn) {
        await this.$router.replace(LinksConstrains.OOC_LOGIN).catch(() => {});
      }
    },

    /**
     * Handle resizing window
     * @return {void}
     */
    handleOnResize() {
      console.log("screen width = ", window.innerWidth);
    },

    /**
     * Handle on app scrolling event
     * @param {Object} e
     */
    handleOnScroll(e) {
      if (typeof window === "undefined") return;
      const top = window.pageYOffset || e.target?.scrollTop || 0;
      this.isAsideFullHeight = top > 20;
    },

    /**
     * Handle on app version changed
     * @return {void}
     */
    handleOnAppStorageVersion() {
      if (Storage.get(StorageConstants.APP_STORAGE_VERSION) === process.env.VUE_APP_STORAGE_VERSION) {
        return;
      }

      const credentials = Storage.get(StorageConstants.CREDENTIALS) || null;

      Storage.clearAll();
      Storage.set(StorageConstants.APP_STORAGE_VERSION, process.env.VUE_APP_STORAGE_VERSION);
      if (!!credentials) {
        Storage.set(StorageConstants.CREDENTIALS, credentials);
      }

      this.setUserAuthorized(false);
      this.setUserMessage({ success: false, message: this.$t("session_timeout") });
    },
  },
});
</script>
